import { AttributeSelectorType, AttributeType } from "@evergis/api";
import { Checkbox, Dropdown, Input, IOption, Link, NumberInput } from "@evergis/ui";
import React, { FC, ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatAttributeValue } from "../../../../../utils/formatAttributeValue";
import { NumberFormState } from "../hooks/useAttributeFormat";
import { updateNumberFormat } from "../hooks/useUpdateAttributeFormat";

import { numberOptions } from "./FormatSettingsOptions";
import { FormSettingsContainer, Hint, SettingsRow, SmallHintText } from "./styled";

type NumberFormatSettingsProps = {
  formState: NumberFormState;
  type: AttributeType;
  attributeSelectorType?: AttributeSelectorType;
  childrenBefore?: ReactNode;
  onChangeFormatValue: (option: IOption[]) => void;
  onChangeNumberInput: (value: number) => void;
  onChangeFormatLabel: (evt: React.ChangeEvent<HTMLInputElement>) => void;
  toggleSelected: (name: string) => void;
};

const defaultValue = numberOptions[0].value;

export const NumberFormatSettings: FC<NumberFormatSettingsProps> = ({
  formState,
  type,
  childrenBefore,
  attributeSelectorType,
  onChangeFormatValue,
  onChangeNumberInput,
  onChangeFormatLabel,
  toggleSelected,
  children,
}) => {
  const { t } = useTranslation("dialogs");
  const isDouble = type === AttributeType.Double;
  const example = 1234578.9111223232;

  const disabledRounding = useMemo(
    () => !isDouble && (!formState.format || formState.format === defaultValue),
    [isDouble, formState.format],
  );
  const formattedExample = useMemo(
    () => formatAttributeValue(type, example, updateNumberFormat(formState)),
    [type, example, formState],
  );

  return (
    <FormSettingsContainer height={"unset"}>
      {childrenBefore}
      <SettingsRow>
        <Dropdown
          label={`${t("dialogComponents.display")}:`}
          options={numberOptions.map(item => ({
            ...item,
            text: t(item.localizationKey, { ns: "common" }),
          }))}
          value={formState.format || defaultValue}
          width={"11.8125rem"}
          zIndex={10000}
          onChange={onChangeFormatValue}
        />
        <NumberInput
          label={`${t("dialogComponents.roundTo")}:`}
          min={0}
          max={10}
          width={"5.93rem"}
          value={formState.rounding}
          disabled={disabledRounding}
          counter
          onChange={onChangeNumberInput}
        />
      </SettingsRow>
      <Input
        label={t("dialogComponents.units")}
        value={formState.label}
        width={"18.5rem"}
        onChange={onChangeFormatLabel}
      />
      <Checkbox
        label={t("dialogComponents.splitDigitGroup")}
        checked={formState.splitDigitGroup}
        onChange={() => toggleSelected("splitDigitGroup")}
      />
      <Hint>
        <SmallHintText>{t("dialogComponents.example")}:</SmallHintText>
        {(!attributeSelectorType || attributeSelectorType === AttributeSelectorType.None) && formattedExample}
        {attributeSelectorType === AttributeSelectorType.SelectFromHandBook && (
          <div>{t("recordFromDictionary", { ns: "common" })}</div>
        )}
        {attributeSelectorType === AttributeSelectorType.ViewHandBook && (
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          <Link>{formattedExample}</Link>
        )}
      </Hint>
      {children}
    </FormSettingsContainer>
  );
};
